<template>
  <button
    type="button"
    class="work-list-item"
    :class="{ 'work-list-item--masked': isScriptModeMasked }"
    :disabled="disabled"
    :aria-disabled="isScriptModeMasked"
    @click="$emit('select')"
  >
    <div class="work-list-item__cover">
      <image-view class="work-list-item__image" :key="coverImageVersion" :src="imageSrc" placeholder="/img/placeholders/novel.png" />
    </div>
    <div class="work-list-item__info">
      <span class="work-list-item__title">{{ novel.title }}</span>
      <div class="work-list-item__meta">
        <span v-if="workStatusLabel" class="work-list-item__status">{{ workStatusLabel }}</span>
        <span class="work-list-item__date">
          <icon-pencil class="work-list-item__date-icon" />
          {{ createdAtLabel }}
        </span>
        <span class="work-list-item__date">
          <icon-reload class="work-list-item__date-icon" />
          {{ updatedAtLabel }}
        </span>
      </div>
      <div v-if="novel.groups && novel.groups.length > 0" class="work-list-item__groups">
        <span v-for="group in novel.groups" :key="group.groupId" class="work-list-item__group">
          <icon-folder-heart class="work-list-item__group-icon" />
          {{ group.name }}
        </span>
      </div>
    </div>
  </button>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import format from "date-fns/format";
import ImageView from "@/components/atoms/ImageView.vue";
import { Novel } from "@/lib/models";
import { getTranslatedWorkStatusById } from "@/lib/utils/novelSettingsHelper";
import IconFolderHeart from "~icons/figma/FolderHeart";
import IconPencil from "~icons/figma/Pencil";
import IconReload from "~icons/figma/Reload";

export default Vue.extend({
  name: "AssistantCatHomeWorkListItem",
  components: {
    ImageView,
    IconFolderHeart,
    IconPencil,
    IconReload,
  },
  props: {
    novel: {
      type: Object as PropType<Novel>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isScriptModeMasked: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    imageSrc(): string | undefined {
      const { novelId, image } = this.novel;
      if (image && image.startsWith("file:")) {
        return `file:novels/${novelId}/cover.jpg`;
      }
      return image;
    },
    coverImageVersion(): number {
      return this.$store.getters["novelModule/coverImageVersion"](this.novel.novelId);
    },
    workStatusLabel(): string {
      const workStatus = this.novel.workStatus || "inProgress";
      return getTranslatedWorkStatusById((key: string) => this.$t(key) as string, workStatus);
    },
    createdAtLabel(): string {
      return this.novel.createdAt ? format(this.novel.createdAt, "YYYY/MM/DD") : "-";
    },
    updatedAtLabel(): string {
      return this.novel.updatedAtLatestDataInNovel ? format(this.novel.updatedAtLatestDataInNovel, "YYYY/MM/DD") : "-";
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.work-list-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  text-align: left;
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &:disabled {
    cursor: wait;
  }
}

.work-list-item--masked {
  opacity: 0.44;
  cursor: not-allowed;

  &::after {
    display: none;
  }
}

.work-list-item__cover {
  flex-shrink: 0;
  width: 72px;
  height: 107px;
  overflow: hidden;
  border-radius: var(--radius-xs);
}

.work-list-item__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.work-list-item__info {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-self: stretch;
  min-width: 0;
  padding-top: 2px;
}

.work-list-item__title {
  display: -webkit-box;
  overflow: hidden;
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.work-list-item__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: auto;
}

.work-list-item__status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: var(--radius-rounded);
  background-color: var(--color-background-invert);
  color: var(--color-text-invert);
  font-size: var(--font-size-xxs);
  font-weight: var(--font-weight-bold);
  line-height: 14px;
  white-space: nowrap;
}

.work-list-item__date {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--color-text-low);
  font-size: var(--font-size-xs);
  line-height: 1;
  white-space: nowrap;
}

.work-list-item__date-icon {
  flex-shrink: 0;
  font-size: var(--font-size-sm);
}

.work-list-item__groups {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
  margin-top: 6px;
}

.work-list-item__group {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-xxs);
  line-height: 14px;
  white-space: nowrap;
}

.work-list-item__group-icon {
  flex-shrink: 0;
  font-size: var(--font-size-xs);
}
</style>
